import api from './api';

/**
 * Micro simulations — catalogue, trainer launches, and the participant's run.
 *
 * Participant calls include the session id in the path on purpose: api.js uses it to
 * pick that session's stored identity and send the signed X-Participant-Token, and the
 * server checks the same id against the token.
 *
 * The server is authoritative for turns, outcomes and scores. Every mutating call
 * returns the caller's fresh state; screens render that instead of guessing.
 */

/** A per-turn id so a retried request is recognised server-side (idempotent). */
export const newTurnId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch { /* fall through */ }
  return `t-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const microSimService = {
  // ---- catalogue (trainer) ----
  list: () => api.get('/micro-sims'),
  get: (slug) => api.get(`/micro-sims/${encodeURIComponent(slug)}`),

  // ---- preview: the trainer plays both seats; nothing is saved to any report ----
  // versionId: super-admins previewing an unpublished draft.
  startPreview: (slug, { industry, roleLabels, versionId } = {}) =>
    api.post(`/micro-sims/${encodeURIComponent(slug)}/preview-runs`, { industry, roleLabels, versionId }),
  previewState: (runId, slot) => api.get(`/micro-sims/preview-runs/${runId}/state`, { slot }),
  previewChoose: (runId, slot, turn) =>
    api.post(`/micro-sims/preview-runs/${runId}/choices`, { slot, ...turn }),
  previewContinue: (runId, slot, round) =>
    api.post(`/micro-sims/preview-runs/${runId}/continue`, { slot, round }),
  previewReport: (runId, slot) => api.get(`/micro-sims/preview-runs/${runId}/report`, { slot }),

  // ---- launches (trainer, during a live session) ----
  activeLaunch: (sessionId) => api.get('/micro-sim-launches/active', { sessionId }),
  // Everyone in the room with their session numbers, for choosing pairs before launch.
  candidates: (sessionId) => api.get('/micro-sim-launches/candidates', { sessionId }),
  // pairing: omitted for random pairs, or
  //   { mode: 'manual', pairs: [[idA, idB]], observers: [{ participantId, pairIndex }], pairRestRandomly }
  launch: ({ slug, sessionId, industry, roleLabels, pairing }) =>
    api.post('/micro-sim-launches', { slug, sessionId, industry, roleLabels, pairing }),
  getLaunch: (launchId) => api.get(`/micro-sim-launches/${launchId}`),
  pairLateJoiners: (launchId) => api.post(`/micro-sim-launches/${launchId}/pair-late-joiners`),
  // The trainer pairs waiting people by hand. Observers may join a running pair by runId.
  pairManually: (launchId, { pairs, observers, pairRestRandomly }) =>
    api.post(`/micro-sim-launches/${launchId}/pairs`, { pairs, observers, pairRestRandomly }),
  reshuffle: (launchId) => api.post(`/micro-sim-launches/${launchId}/reshuffle`),
  endLaunch: (launchId) => api.post(`/micro-sim-launches/${launchId}/end`),

  // ---- results and history (staff: everyone's scores) ----
  history: ({ sessionId, limit } = {}) =>
    api.get('/micro-sim-launches', sessionId ? { sessionId, ...(limit ? { limit } : {}) } : { mine: 'true', ...(limit ? { limit } : {}) }),
  results: (launchId) => api.get(`/micro-sim-launches/${launchId}/results`),
  downloadResultsCsv: (launchId) => api.download(`/micro-sim-launches/${launchId}/results.csv`, 'micro-sim-results.csv'),
  downloadGroupPdf: (launchId) => api.download(`/micro-sim-launches/${launchId}/report.pdf`, 'micro-sim-group-results.pdf'),
  personReport: (launchId, runId, slot) => api.get(`/micro-sim-launches/${launchId}/runs/${runId}/report`, { slot }),
  downloadPersonPdf: (launchId, runId, slot) =>
    api.download(`/micro-sim-launches/${launchId}/runs/${runId}/report.pdf?slot=${slot}`, 'micro-sim-report.pdf'),
  participantResults: (sessionId, participantId) =>
    api.get(`/micro-sim-launches/sessions/${sessionId}/participants/${participantId}/results`),

  // ---- the participant's own run (signed participant token) ----
  mine: (sessionId) => api.get(`/micro-sim-runs/sessions/${sessionId}/mine`),
  state: (sessionId, runId) => api.get(`/micro-sim-runs/sessions/${sessionId}/runs/${runId}/state`),
  choose: (sessionId, runId, turn) =>
    api.post(`/micro-sim-runs/sessions/${sessionId}/runs/${runId}/choices`, turn),
  continueRound: (sessionId, runId, round) =>
    api.post(`/micro-sim-runs/sessions/${sessionId}/runs/${runId}/continue`, { round }),
  report: (sessionId, runId) => api.get(`/micro-sim-runs/sessions/${sessionId}/runs/${runId}/report`),
  downloadMyReportPdf: (sessionId, runId) =>
    api.download(`/micro-sim-runs/sessions/${sessionId}/runs/${runId}/report.pdf`, 'my-micro-sim-report.pdf'),
  completed: (sessionId) => api.get(`/micro-sim-runs/sessions/${sessionId}/completed`),
};

export default microSimService;
